export type DynamicBooksStructuredMediaFormat = "svg" | "png";
export type DynamicBooksStructuredMediaHost = "dynamicbooks.se" | "dynamicbooks.app";
export type DynamicBooksStructuredMediaLocale = "sv" | "en";
export type DynamicBooksStructuredMediaSchemaUsage =
  | "Article.image"
  | "LinkedIn.share_image"
  | "MerchantReturnPolicy.visible_policy_icon"
  | "OfferShippingDetails.visible_policy_icon"
  | "OpenGraph.og:image"
  | "Product.image"
  | "SoftwareApplication.image"
  | "Twitter.summary_large_image"
  | "WebPage.primaryImageOfPage";

export type DynamicBooksStructuredMediaUsageRole =
  | "article_image"
  | `asset_group:${string}`
  | "linkedin_share_image"
  | "merchant_return_policy_visible_icon"
  | "offer_shipping_visible_icon"
  | "open_graph_image"
  | "product_image"
  | "software_application_image"
  | "twitter_summary_large_image"
  | "webpage_primary_image";

export type DynamicBooksStructuredMediaFormatEntry = {
  readonly publicPath: `/assets/dynamicbooks-v4-structured-media/${string}.${DynamicBooksStructuredMediaFormat}`;
  readonly width: number;
  readonly height: number;
  readonly contentType: "image/svg+xml" | "image/png";
};

export type DynamicBooksStructuredMediaImageObject = {
  "@type": "ImageObject";
  url: string;
  contentUrl: string;
  width: number;
  height: number;
  caption: string;
};

export type DynamicBooksStructuredMediaAsset = {
  readonly id: string;
  readonly family:
    | "article-images"
    | "locale-og"
    | "policy-icons"
    | "social-variants"
    | "software-overview"
    | "tier-products"
    | "workflow-diagrams";
  readonly title: string;
  readonly alt: string;
  readonly dimensions: {
    readonly width: number;
    readonly height: number;
  };
  readonly schemaUsage: readonly DynamicBooksStructuredMediaSchemaUsage[];
  readonly usageRoles: readonly DynamicBooksStructuredMediaUsageRole[];
  readonly formats: Record<DynamicBooksStructuredMediaFormat, DynamicBooksStructuredMediaFormatEntry>;
};

export type DynamicBooksOrganizationLogoAsset = {
  readonly id: "organization-logo";
  readonly title: "DynamicBooks organization logo";
  readonly alt: "DynamicBooks";
  readonly schemaUsage: readonly ["Organization.logo"];
  readonly usageRoles: readonly ["organization_logo"];
  readonly formats: {
    readonly png: {
      readonly publicPath: "/assets/brand/v3/dynamicbooks-favicon-512.png";
      readonly width: 512;
      readonly height: 512;
      readonly contentType: "image/png";
    };
  };
};

export const dynamicBooksStructuredMediaOrigins = {
  "dynamicbooks.se": "https://dynamicbooks.se",
  "dynamicbooks.app": "https://dynamicbooks.app"
} as const satisfies Record<DynamicBooksStructuredMediaHost, `https://${DynamicBooksStructuredMediaHost}`>;

export const dynamicBooksOrganizationLogoAsset = {
  id: "organization-logo",
  title: "DynamicBooks organization logo",
  alt: "DynamicBooks",
  schemaUsage: ["Organization.logo"],
  usageRoles: ["organization_logo"],
  formats: {
    png: {
      publicPath: "/assets/brand/v3/dynamicbooks-favicon-512.png",
      width: 512,
      height: 512,
      contentType: "image/png"
    }
  }
} as const satisfies DynamicBooksOrganizationLogoAsset;

export const dynamicBooksStructuredMediaAssets = [
  {
    id: "article-agentdriven-bokforing-16x9",
    family: "article-images",
    title: "DynamicBooks artikelbild för agentdriven bokföring",
    alt: "DynamicBooks artikelbild med agentdriven bokföring, Data Rooms, torrkörning och granskning.",
    dimensions: { width: 1600, height: 900 },
    schemaUsage: ["Article.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["article_image", "asset_group:article-images", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-article-agentdriven-bokforing-16x9.svg",
        width: 1600,
        height: 900,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-article-agentdriven-bokforing-16x9.png",
        width: 1600,
        height: 900,
        contentType: "image/png"
      }
    }
  },
  {
    id: "article-agentdriven-bokforing-1x1",
    family: "article-images",
    title: "DynamicBooks artikelbild för agentdriven bokföring",
    alt: "DynamicBooks artikelbild med agentdriven bokföring, Data Rooms, torrkörning och granskning.",
    dimensions: { width: 1200, height: 1200 },
    schemaUsage: ["Article.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["article_image", "asset_group:article-images", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-article-agentdriven-bokforing-1x1.svg",
        width: 1200,
        height: 1200,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-article-agentdriven-bokforing-1x1.png",
        width: 1200,
        height: 1200,
        contentType: "image/png"
      }
    }
  },
  {
    id: "article-agentdriven-bokforing-4x3",
    family: "article-images",
    title: "DynamicBooks artikelbild för agentdriven bokföring",
    alt: "DynamicBooks artikelbild med agentdriven bokföring, Data Rooms, torrkörning och granskning.",
    dimensions: { width: 1200, height: 900 },
    schemaUsage: ["Article.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["article_image", "asset_group:article-images", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-article-agentdriven-bokforing-4x3.svg",
        width: 1200,
        height: 900,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-article-agentdriven-bokforing-4x3.png",
        width: 1200,
        height: 900,
        contentType: "image/png"
      }
    }
  },
  {
    id: "diagram-accounting-agent-workflow",
    family: "workflow-diagrams",
    title: "DynamicBooks accounting-agent workflow diagram",
    alt: "DynamicBooks accounting-agent workflow diagram with prepare, dry run, review, confirm, and evidence steps.",
    dimensions: { width: 1600, height: 900 },
    schemaUsage: ["SoftwareApplication.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["asset_group:workflow-diagrams", "software_application_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-diagram-accounting-agent-workflow.svg",
        width: 1600,
        height: 900,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-diagram-accounting-agent-workflow.png",
        width: 1600,
        height: 900,
        contentType: "image/png"
      }
    }
  },
  {
    id: "diagram-data-room-evidence-loop",
    family: "workflow-diagrams",
    title: "DynamicBooks Data Rooms evidence-loop diagram",
    alt: "DynamicBooks Data Rooms evidence-loop diagram with records, dry-run, checks, evidence, and scope control.",
    dimensions: { width: 1600, height: 900 },
    schemaUsage: ["SoftwareApplication.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["asset_group:workflow-diagrams", "software_application_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-diagram-data-room-evidence-loop.svg",
        width: 1600,
        height: 900,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-diagram-data-room-evidence-loop.png",
        width: 1600,
        height: 900,
        contentType: "image/png"
      }
    }
  },
  {
    id: "locale-og-en",
    family: "locale-og",
    title: "DynamicBooks English preview image",
    alt: "DynamicBooks preview image with accounting workspace, BAS ledgers, VAT checks, and Data Rooms.",
    dimensions: { width: 1200, height: 630 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "WebPage.primaryImageOfPage", "Product.image"],
    usageRoles: ["asset_group:locale-og", "open_graph_image", "product_image", "twitter_summary_large_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-og-en.svg",
        width: 1200,
        height: 630,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-og-en.png",
        width: 1200,
        height: 630,
        contentType: "image/png"
      }
    }
  },
  {
    id: "locale-og-sv",
    family: "locale-og",
    title: "DynamicBooks svensk förhandsbild",
    alt: "DynamicBooks förhandsbild med bokföringsworkspace, BAS, moms och Data Rooms.",
    dimensions: { width: 1200, height: 630 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "WebPage.primaryImageOfPage", "Product.image"],
    usageRoles: ["asset_group:locale-og", "open_graph_image", "product_image", "twitter_summary_large_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-og-sv.svg",
        width: 1200,
        height: 630,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-og-sv.png",
        width: 1200,
        height: 630,
        contentType: "image/png"
      }
    }
  },
  {
    id: "policy-digital-delivery",
    family: "policy-icons",
    title: "DynamicBooks digital delivery policy icon",
    alt: "DynamicBooks digital delivery icon showing software access without physical shipment.",
    dimensions: { width: 512, height: 512 },
    schemaUsage: ["OfferShippingDetails.visible_policy_icon"],
    usageRoles: ["asset_group:policy-icons", "offer_shipping_visible_icon"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-policy-digital-delivery.svg",
        width: 512,
        height: 512,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-policy-digital-delivery.png",
        width: 512,
        height: 512,
        contentType: "image/png"
      }
    }
  },
  {
    id: "policy-no-return",
    family: "policy-icons",
    title: "DynamicBooks software access policy icon",
    alt: "DynamicBooks software access policy icon for provisioned digital access.",
    dimensions: { width: 512, height: 512 },
    schemaUsage: ["MerchantReturnPolicy.visible_policy_icon"],
    usageRoles: ["asset_group:policy-icons", "merchant_return_policy_visible_icon"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-policy-no-return.svg",
        width: 512,
        height: 512,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-policy-no-return.png",
        width: 512,
        height: 512,
        contentType: "image/png"
      }
    }
  },
  {
    id: "social-linkedin-en",
    family: "social-variants",
    title: "DynamicBooks English LinkedIn image",
    alt: "DynamicBooks English LinkedIn preview image with accounting workflow and Data Rooms.",
    dimensions: { width: 1200, height: 627 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "LinkedIn.share_image"],
    usageRoles: ["asset_group:social-variants", "linkedin_share_image", "open_graph_image", "twitter_summary_large_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-linkedin-en.svg",
        width: 1200,
        height: 627,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-linkedin-en.png",
        width: 1200,
        height: 627,
        contentType: "image/png"
      }
    }
  },
  {
    id: "social-linkedin-sv",
    family: "social-variants",
    title: "DynamicBooks svensk LinkedIn bild",
    alt: "DynamicBooks svensk LinkedIn förhandsbild med bokföringsflöde och Data Rooms.",
    dimensions: { width: 1200, height: 627 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "LinkedIn.share_image"],
    usageRoles: ["asset_group:social-variants", "linkedin_share_image", "open_graph_image", "twitter_summary_large_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-linkedin-sv.svg",
        width: 1200,
        height: 627,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-linkedin-sv.png",
        width: 1200,
        height: 627,
        contentType: "image/png"
      }
    }
  },
  {
    id: "social-portrait-en",
    family: "social-variants",
    title: "DynamicBooks English portrait social image",
    alt: "DynamicBooks English portrait social preview image with accounting workflow and Data Rooms.",
    dimensions: { width: 1080, height: 1350 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "LinkedIn.share_image"],
    usageRoles: ["asset_group:social-variants", "linkedin_share_image", "open_graph_image", "twitter_summary_large_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-portrait-en.svg",
        width: 1080,
        height: 1350,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-portrait-en.png",
        width: 1080,
        height: 1350,
        contentType: "image/png"
      }
    }
  },
  {
    id: "social-portrait-sv",
    family: "social-variants",
    title: "DynamicBooks svensk stående social bild",
    alt: "DynamicBooks svensk stående social förhandsbild med bokföringsflöde och Data Rooms.",
    dimensions: { width: 1080, height: 1350 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "LinkedIn.share_image"],
    usageRoles: ["asset_group:social-variants", "linkedin_share_image", "open_graph_image", "twitter_summary_large_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-portrait-sv.svg",
        width: 1080,
        height: 1350,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-portrait-sv.png",
        width: 1080,
        height: 1350,
        contentType: "image/png"
      }
    }
  },
  {
    id: "social-square-en",
    family: "social-variants",
    title: "DynamicBooks English square social image",
    alt: "DynamicBooks English square social preview image with accounting workflow and Data Rooms.",
    dimensions: { width: 1080, height: 1080 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "LinkedIn.share_image"],
    usageRoles: ["asset_group:social-variants", "linkedin_share_image", "open_graph_image", "twitter_summary_large_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-square-en.svg",
        width: 1080,
        height: 1080,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-square-en.png",
        width: 1080,
        height: 1080,
        contentType: "image/png"
      }
    }
  },
  {
    id: "social-square-sv",
    family: "social-variants",
    title: "DynamicBooks svensk kvadratisk social bild",
    alt: "DynamicBooks svensk kvadratisk social förhandsbild med bokföringsflöde och Data Rooms.",
    dimensions: { width: 1080, height: 1080 },
    schemaUsage: ["OpenGraph.og:image", "Twitter.summary_large_image", "LinkedIn.share_image"],
    usageRoles: ["asset_group:social-variants", "linkedin_share_image", "open_graph_image", "twitter_summary_large_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-square-sv.svg",
        width: 1080,
        height: 1080,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-social-square-sv.png",
        width: 1080,
        height: 1080,
        contentType: "image/png"
      }
    }
  },
  {
    id: "software-overview",
    family: "software-overview",
    title: "DynamicBooks SoftwareApplication overview",
    alt: "DynamicBooks accounting software overview with reports, workflow lanes, and review chips.",
    dimensions: { width: 1600, height: 900 },
    schemaUsage: ["SoftwareApplication.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["asset_group:software-overview", "software_application_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-software-overview.svg",
        width: 1600,
        height: 900,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-software-overview.png",
        width: 1600,
        height: 900,
        contentType: "image/png"
      }
    }
  },
  {
    id: "tier-basic",
    family: "tier-products",
    title: "DynamicBooks Basic tier product image",
    alt: "DynamicBooks Basic tier image with BAS ledger, VAT material, and core reports.",
    dimensions: { width: 1200, height: 1200 },
    schemaUsage: ["Product.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["asset_group:tier-products", "product_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-tier-basic.svg",
        width: 1200,
        height: 1200,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-tier-basic.png",
        width: 1200,
        height: 1200,
        contentType: "image/png"
      }
    }
  },
  {
    id: "tier-business",
    family: "tier-products",
    title: "DynamicBooks Business tier product image",
    alt: "DynamicBooks Business tier image with bank and SIE, AP/AR overview, and Data Rooms.",
    dimensions: { width: 1200, height: 1200 },
    schemaUsage: ["Product.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["asset_group:tier-products", "product_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-tier-business.svg",
        width: 1200,
        height: 1200,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-tier-business.png",
        width: 1200,
        height: 1200,
        contentType: "image/png"
      }
    }
  },
  {
    id: "tier-premium",
    family: "tier-products",
    title: "DynamicBooks Premium tier product image",
    alt: "DynamicBooks Premium tier image with report packages, agent preparation, payroll, and MCP.",
    dimensions: { width: 1200, height: 1200 },
    schemaUsage: ["Product.image", "WebPage.primaryImageOfPage"],
    usageRoles: ["asset_group:tier-products", "product_image", "webpage_primary_image"],
    formats: {
      svg: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-tier-premium.svg",
        width: 1200,
        height: 1200,
        contentType: "image/svg+xml"
      },
      png: {
        publicPath: "/assets/dynamicbooks-v4-structured-media/dynamicbooks-v4-tier-premium.png",
        width: 1200,
        height: 1200,
        contentType: "image/png"
      }
    }
  }
] as const satisfies readonly DynamicBooksStructuredMediaAsset[];

export type DynamicBooksStructuredMediaAssetEntry = (typeof dynamicBooksStructuredMediaAssets)[number];
export type DynamicBooksStructuredMediaAssetId = DynamicBooksStructuredMediaAssetEntry["id"];
export type DynamicBooksStructuredMediaFamily = DynamicBooksStructuredMediaAssetEntry["family"];

const dynamicBooksStructuredMediaAssetMap = new Map<DynamicBooksStructuredMediaAssetId, DynamicBooksStructuredMediaAssetEntry>(
  dynamicBooksStructuredMediaAssets.map((asset) => [asset.id, asset])
);

export const dynamicBooksStructuredMediaRegistry = {
  schemaVersion: "dynamicbooks-v4-public-media-registry-v1",
  outputRoot: "/assets/dynamicbooks-v4-structured-media/",
  assetCount: dynamicBooksStructuredMediaAssets.length,
  assets: dynamicBooksStructuredMediaAssets
} as const;

export function getDynamicBooksStructuredMediaAsset(
  assetId: DynamicBooksStructuredMediaAssetId
): DynamicBooksStructuredMediaAssetEntry {
  const asset = dynamicBooksStructuredMediaAssetMap.get(assetId);
  if (!asset) {
    throw new Error(`Unknown DynamicBooks structured media asset: ${assetId}`);
  }
  return asset;
}

export function getDynamicBooksStructuredMediaFormat(
  assetId: DynamicBooksStructuredMediaAssetId,
  format: DynamicBooksStructuredMediaFormat
): DynamicBooksStructuredMediaFormatEntry {
  return getDynamicBooksStructuredMediaAsset(assetId).formats[format];
}

export function dynamicBooksStructuredMediaHostForLocale(
  locale: DynamicBooksStructuredMediaLocale
): DynamicBooksStructuredMediaHost {
  return locale === "en" ? "dynamicbooks.app" : "dynamicbooks.se";
}

export function dynamicBooksStructuredMediaHostFromOrigin(originOrHost: string): DynamicBooksStructuredMediaHost {
  const candidate = originOrHost.trim();
  if (!candidate) {
    return "dynamicbooks.se";
  }
  try {
    const hostname = candidate.includes("://") ? new URL(candidate).hostname : candidate;
    return hostname === "dynamicbooks.app" || hostname === "www.dynamicbooks.app"
      ? "dynamicbooks.app"
      : "dynamicbooks.se";
  } catch {
    return "dynamicbooks.se";
  }
}

export function buildDynamicBooksStructuredMediaUrl(
  assetId: DynamicBooksStructuredMediaAssetId,
  format: DynamicBooksStructuredMediaFormat,
  host: DynamicBooksStructuredMediaHost
): string {
  return new URL(getDynamicBooksStructuredMediaFormat(assetId, format).publicPath, dynamicBooksStructuredMediaOrigins[host]).toString();
}

export function buildDynamicBooksStructuredMediaUrlForOrigin(
  assetId: DynamicBooksStructuredMediaAssetId,
  format: DynamicBooksStructuredMediaFormat,
  originOrUrl: string
): string {
  return new URL(getDynamicBooksStructuredMediaFormat(assetId, format).publicPath, normalizeStructuredMediaOrigin(originOrUrl)).toString();
}

export function buildDynamicBooksStructuredMediaUrlForLocale(
  assetId: DynamicBooksStructuredMediaAssetId,
  format: DynamicBooksStructuredMediaFormat,
  locale: DynamicBooksStructuredMediaLocale
): string {
  return buildDynamicBooksStructuredMediaUrl(assetId, format, dynamicBooksStructuredMediaHostForLocale(locale));
}

export function buildDynamicBooksStructuredMediaUrls(
  assetId: DynamicBooksStructuredMediaAssetId,
  host: DynamicBooksStructuredMediaHost
): Record<DynamicBooksStructuredMediaFormat, string> {
  return {
    svg: buildDynamicBooksStructuredMediaUrl(assetId, "svg", host),
    png: buildDynamicBooksStructuredMediaUrl(assetId, "png", host)
  };
}

export function buildDynamicBooksStructuredMediaImageObject(
  assetId: DynamicBooksStructuredMediaAssetId,
  format: DynamicBooksStructuredMediaFormat,
  host: DynamicBooksStructuredMediaHost
): DynamicBooksStructuredMediaImageObject {
  const asset = getDynamicBooksStructuredMediaAsset(assetId);
  const media = asset.formats[format];
  const url = buildDynamicBooksStructuredMediaUrl(assetId, format, host);

  return {
    "@type": "ImageObject",
    url,
    contentUrl: url,
    width: media.width,
    height: media.height,
    caption: asset.alt
  };
}

export function buildDynamicBooksStructuredMediaImageObjectForOrigin(
  assetId: DynamicBooksStructuredMediaAssetId,
  format: DynamicBooksStructuredMediaFormat,
  originOrUrl: string
): DynamicBooksStructuredMediaImageObject {
  const asset = getDynamicBooksStructuredMediaAsset(assetId);
  const media = asset.formats[format];
  const url = buildDynamicBooksStructuredMediaUrlForOrigin(assetId, format, originOrUrl);

  return {
    "@type": "ImageObject",
    url,
    contentUrl: url,
    width: media.width,
    height: media.height,
    caption: asset.alt
  };
}

export function buildDynamicBooksOrganizationLogoUrlForOrigin(originOrUrl: string): string {
  return new URL(dynamicBooksOrganizationLogoAsset.formats.png.publicPath, normalizeStructuredMediaOrigin(originOrUrl)).toString();
}

export function buildDynamicBooksOrganizationLogoImageObjectForOrigin(
  originOrUrl: string
): DynamicBooksStructuredMediaImageObject {
  const media = dynamicBooksOrganizationLogoAsset.formats.png;
  const url = buildDynamicBooksOrganizationLogoUrlForOrigin(originOrUrl);

  return {
    "@type": "ImageObject",
    url,
    contentUrl: url,
    width: media.width,
    height: media.height,
    caption: dynamicBooksOrganizationLogoAsset.alt
  };
}

export function listDynamicBooksStructuredMediaBySchemaUsage(
  schemaUsage: DynamicBooksStructuredMediaSchemaUsage
): readonly DynamicBooksStructuredMediaAssetEntry[] {
  return dynamicBooksStructuredMediaAssets.filter((asset) => (
    asset.schemaUsage as readonly DynamicBooksStructuredMediaSchemaUsage[]
  ).includes(schemaUsage));
}

function normalizeStructuredMediaOrigin(originOrUrl: string): string {
  const candidate = originOrUrl.trim();

  if (!candidate) {
    return dynamicBooksStructuredMediaOrigins["dynamicbooks.se"];
  }

  const parseCandidate = candidate.includes("://") ? candidate : `https://${candidate}`;

  try {
    const parsed = new URL(parseCandidate);

    if (parsed.protocol === "https:" || parsed.protocol === "http:") {
      return parsed.origin;
    }
  } catch {
    return dynamicBooksStructuredMediaOrigins[dynamicBooksStructuredMediaHostFromOrigin(originOrUrl)];
  }

  return dynamicBooksStructuredMediaOrigins[dynamicBooksStructuredMediaHostFromOrigin(originOrUrl)];
}
